import { isAthlete, isGuardian } from "./auth";
import { isPostLoginNextAllowed } from "./loginRedirect";

/**
 * Profile id of the guardian's sole linked athlete, read from the `guardianAthletes` payload cached
 * at login. Used as a fallback when no explicit athlete has been selected, so a single-athlete
 * guardian resolves correctly even if they reach a page without going through `/guardian/select`
 * (deep link, reload, direct navigation). Returns null when there is not exactly one linked athlete.
 */
function getSoleLinkedGuardianAthleteId(): string | null {
    try {
        const raw = localStorage.getItem("guardianAthletes");
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as Array<{ athleteId?: number | string }>;
        if (!Array.isArray(parsed) || parsed.length !== 1) {
            return null;
        }
        const id = parsed[0]?.athleteId;
        return id != null && String(id).trim() ? String(id).trim() : null;
    } catch {
        return null;
    }
}

/**
 * Profile id of the athlete a guardian is currently viewing, if any. Prefers the explicit selection
 * (`guardianViewingAthleteId`) and falls back to the guardian's only linked athlete so guardian
 * views (game plan, media kit, etc.) still resolve when no selection has been persisted yet.
 *
 * Does not pick among multiple linked athletes — use {@link ensureGuardianViewingAthleteId} when a
 * page or login must persist the same first-athlete default as `/guardian/select`.
 */
export function getGuardianViewingAthleteId(): string | null {
    const id = localStorage.getItem("guardianViewingAthleteId")?.trim();
    if (id) {
        return id;
    }
    return getSoleLinkedGuardianAthleteId();
}

function parseGuardianAthletes(): Array<{ athleteId?: number | string }> {
    try {
        const raw = localStorage.getItem("guardianAthletes");
        if (!raw) {
            return [];
        }
        const parsed = JSON.parse(raw) as Array<{ athleteId?: number | string }>;
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

/**
 * Persist a viewing id when a guardian reached an athlete route without going through
 * `/guardian/select` (login `next=/athlete/home`, bookmark, reload). Matches select-page
 * behaviour: keep an existing choice, otherwise take the first linked athlete.
 */
export function ensureGuardianViewingAthleteId(): string | null {
    const stored = localStorage.getItem("guardianViewingAthleteId")?.trim();
    if (stored) {
        return stored;
    }
    const existing = getGuardianViewingAthleteId();
    if (existing) {
        localStorage.setItem("guardianViewingAthleteId", existing);
        return existing;
    }
    for (const athlete of parseGuardianAthletes()) {
        const id = athlete?.athleteId;
        if (id != null && String(id).trim()) {
            const value = String(id).trim();
            localStorage.setItem("guardianViewingAthleteId", value);
            return value;
        }
    }
    return null;
}

/**
 * Guardian landing after login. Honour `next` only after a viewing id exists so `/athlete/*`
 * never calls `/api/athletes/me` (404 for a guardian). With no linked athletes, send them
 * through `/guardian/select`, which routes to the no-athletes page.
 */
export function resolveGuardianPostLoginPath(next: string | null | undefined): string {
    const viewingId = ensureGuardianViewingAthleteId();
    if (viewingId && next && isPostLoginNextAllowed("GUARDIAN", next)) {
        return next;
    }
    return "/guardian/select";
}

export function isGuardianViewingAthlete(): boolean {
    return isGuardian() && Boolean(getGuardianViewingAthleteId());
}

/**
 * Athlete self-service profile edit (pencil / save). Guardians never edit, including when a
 * viewing id is stored and the session role was mirrored as athlete-like.
 */
export function canAthleteSelfEditProfile(): boolean {
    if (isGuardian() || getGuardianViewingAthleteId()) {
        return false;
    }
    return isAthlete();
}

/**
 * Resolve the athlete profile id for the current view:
 * admin/staff URL param → guardian localStorage → null (caller should use /me for athletes).
 */
export function resolveViewedAthleteProfileId(routeAthleteId?: string | null): string | null {
    const fromRoute = routeAthleteId?.trim();
    if (fromRoute) {
        return fromRoute;
    }
    return getGuardianViewingAthleteId();
}

/**
 * Path id for college list mutations:
 * `PUT/PATCH/DELETE /api/athletes/{id}/colleges/...`
 *
 * Those endpoints resolve the athlete via {@code getAthleteProfileByUserId}.
 * Pass {@link AthleteProfileView#userId} from the loaded profile (or localStorage
 * {@code userId} for athlete self-view). Never pass AthleteProfile.id or a route
 * profile id — that causes writes to fail or hit the wrong user.
 */
export function resolveAthleteUserIdForCollegeMutation(
    athleteUserIdFromProfile?: string | null,
    localUserId?: string | null,
): string | null {
    const fromProfile = athleteUserIdFromProfile?.trim();
    if (fromProfile) {
        return fromProfile;
    }
    const fromLocal = localUserId?.trim();
    return fromLocal || null;
}

/**
 * True when a value is only digits. College-list APIs key by login username (email),
 * so a numeric User.id must never be used as `{username}` in
 * `/api/colleges/athlete/{username}`.
 */
export function isNumericUserId(value: string | null | undefined): boolean {
    return Boolean(value && /^\d+$/.test(value.trim()));
}

/**
 * First non-empty, non-numeric candidate. Used for college-list GET/POST paths
 * that expect an email/username, not User.id.
 */
export function resolveAthleteCollegeUsername(
    ...candidates: Array<string | null | undefined>
): string {
    for (const candidate of candidates) {
        const trimmed = candidate?.trim();
        if (trimmed && !isNumericUserId(trimmed)) {
            return trimmed;
        }
    }
    return "";
}

/**
 * API path for loading the athlete profile for the current view.
 * Admin/staff: `/api/athletes/{profileId}`
 * Guardian: `/api/athletes/id/{profileId}`
 * Athlete self: `/api/athletes/me`
 */
export function athleteProfileFetchPath(routeAthleteId?: string | null): string | null {
    const fromRoute = routeAthleteId?.trim();
    if (fromRoute) {
        return `/api/athletes/${encodeURIComponent(fromRoute)}`;
    }
    if (isGuardian()) {
        const guardianId = getGuardianViewingAthleteId() ?? ensureGuardianViewingAthleteId();
        if (guardianId) {
            return `/api/athletes/id/${encodeURIComponent(guardianId)}`;
        }
        return null;
    }
    if (isAthlete()) {
        return "/api/athletes/me";
    }
    return null;
}
